import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useShell } from '../components/AppLayout';
import { CustomFieldsScope } from '../hooks/useCustomFields';
import { useRuleSets } from '../hooks/useRuleSets';
import { primaryIdIn } from '../ruleSets';
import { RULE_SETS_PATH, legacyRulesRedirect, type RuleFamily } from '../ruleSetRoutes';
import { RuleManagementScreen } from './RuleManagementScreen';

/**
 * Wave 5 — the routes of the set workspace.
 *
 * `/rule-sets/:setId/transaction|pattern` renders the set's page. `key` forces a remount per
 * set and per tab: without it React reconciles one RuleManagementScreen into the next — same
 * component, same Outlet slot — and an open rule editor drawer survives the navigation,
 * leaving an RB rule being edited on the PB tab (or one set's rule over another set).
 */
export function RuleSetRulesRoute({ kind }: { kind: RuleFamily }) {
  const { setId = '' } = useParams();
  const { state, otherSet } = useShell();
  // Wave 6: the rule editor offers THIS set's custom fields — the other set's draft when it is
  // the one on screen, else the primary's.
  const draft = otherSet?.id === setId && state.loaded?.ruleSetId !== setId ? otherSet.state.draft : state.draft;
  return (
    <CustomFieldsScope fields={draft?.customFields}>
      <RuleManagementScreen key={`${setId}/${kind}`} kind={kind} setId={setId} />
    </CustomFieldsScope>
  );
}

/**
 * The pre-Wave-5 rule screens — bookmarks, old links: `/rules/transaction[?set=x][&rule=y]` →
 * `/rule-sets/<x or the primary>/transaction[?rule=y]`. Without `?set=` it waits for the
 * primary's id (the shell's document, else the registry).
 */
export function LegacyRulesRedirect({ kind }: { kind: RuleFamily }) {
  const shell = useShell();
  const { list } = useRuleSets(shell.settings, { fetch: false });
  const { search } = useLocation();
  const primaryId = shell.state.loaded?.ruleSetId ?? primaryIdIn(list);
  const to = legacyRulesRedirect(kind, search, primaryId);
  if (to) return <Navigate to={to} replace />;
  // No primary to be had (hidden from this account, R8b): the list of the sets it may open.
  if (shell.state.error && list) return <Navigate to={RULE_SETS_PATH} replace />;
  // Still loading: the shell says so (and says why, if its document fails).
  return null;
}
